import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Link } from "@/components/ui";
import { CaptchaModal } from "@/components/waf/CaptchaModal";
import { isWafCaptchaResponse } from "@/hooks/useWafCaptcha";
import { useToast } from "@/hooks/useToast";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
import { LOGIN_PAGE } from "@/router/paths";
import { config } from "@/utils/config";
const trialSchema = z.object({
    fullName: z.string().min(1, "Full name is required"),
    email: z.string().min(1, "Work email is required").email("Please enter a valid email")
});
function getErrorMessage(data) {
    if (data && typeof data === "object" && "message" in data && typeof data.message === "string") {
        return data.message;
    }
    return null;
}
async function createTrialAccount({ data, captchaToken }) {
    const headers = {
        "Content-Type": "application/json"
    };
    if (captchaToken) {
        headers["x-aws-waf-token"] = captchaToken;
    }
    const response = await fetch(`${config.apiUrl}/public/trial`, {
        method: "POST",
        headers,
        body: JSON.stringify(data)
    });
    if (isWafCaptchaResponse(response)) {
        throw new Error("CAPTCHA_REQUIRED");
    }
    if (!response.ok) {
        const errorData = await response.json().catch(()=>null);
        throw new Error(getErrorMessage(errorData) || "Failed to create trial account");
    }
    return response.json();
}
function SuccessMessage({ email, expiryDate }) {
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Trial Account Created",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full text-center",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700",
                    children: /*#__PURE__*/ _jsx("svg", {
                        "aria-hidden": "true",
                        className: "h-7 w-7",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24",
                        children: /*#__PURE__*/ _jsx("path", {
                            d: "M5 13l4 4L19 7",
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: 2
                        })
                    })
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm leading-6 text-gray-600",
                    children: [
                        "We've sent login credentials to ",
                        /*#__PURE__*/ _jsx("strong", {
                            children: email
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "mt-3 text-sm leading-6 text-gray-500",
                    children: [
                        "Your 14-day Qrypt trial expires on",
                        " ",
                        /*#__PURE__*/ _jsx("strong", {
                            children: new Date(expiryDate).toLocaleDateString()
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-6 flex justify-center",
                    children: /*#__PURE__*/ _jsx(Button, {
                        className: "min-w-36",
                        color: "navy-90",
                        href: LOGIN_PAGE,
                        variant: "simple",
                        children: "Go to login"
                    })
                })
            ]
        })
    });
}
export default function TrialPage() {
    const [successData, setSuccessData] = useState(null);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [pendingFormData, setPendingFormData] = useState(null);
    const toast = useToast();
    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(trialSchema)
    });
    const mutation = useMutation({
        mutationFn: createTrialAccount,
        onSuccess: (data)=>{
            setSuccessData({
                email: data.email,
                expiryDate: data.trialExpiryDate
            });
            setShowCaptcha(false);
            setPendingFormData(null);
        },
        onError: (error)=>{
            if (error.message === "CAPTCHA_REQUIRED") {
                setShowCaptcha(true);
            } else {
                toast.error(error.message || "Failed to create trial account");
            }
        }
    });
    function onSubmit(data) {
        setPendingFormData(data);
        mutation.mutate({
            data
        });
    }
    function handleCaptchaSuccess(token) {
        if (pendingFormData) {
            mutation.mutate({
                data: pendingFormData,
                captchaToken: token
            });
        }
        setShowCaptcha(false);
    }
    function handleCaptchaClose() {
        setShowCaptcha(false);
        setPendingFormData(null);
    }
    if (successData) {
        return /*#__PURE__*/ _jsx(SuccessMessage, {
            email: successData.email,
            expiryDate: successData.expiryDate
        });
    }
    return /*#__PURE__*/ _jsxs(AuthPageLayout, {
        title: "Start your Qrypt trial",
        subtitle: "Create an account to explore Qrypt with guided trial access.",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "w-full",
                children: [
                    /*#__PURE__*/ _jsxs("form", {
                        className: "flex flex-col gap-4",
                        onSubmit: handleSubmit(onSubmit),
                        children: [
                            /*#__PURE__*/ _jsx(Input, {
                                label: "Full name",
                                placeholder: "Jane Doe",
                                type: "text",
                                ...register("fullName"),
                                errors: errors.fullName
                            }),
                            /*#__PURE__*/ _jsx(Input, {
                                label: "Work email",
                                placeholder: "jane@company.com",
                                type: "email",
                                ...register("email"),
                                errors: errors.email
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                className: "mt-1 w-full",
                                color: "navy-90",
                                disabled: mutation.isPending,
                                type: "submit",
                                variant: "simple",
                                children: mutation.isPending ? "Starting trial..." : "Start trial"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-6 space-y-3 text-center",
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm text-gray-600",
                                children: [
                                    "Already have an account? ",
                                    /*#__PURE__*/ _jsx(Link, {
                                        href: LOGIN_PAGE,
                                        children: "Log in"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm text-gray-600",
                                children: [
                                    "Need help?",
                                    " ",
                                    /*#__PURE__*/ _jsx(Link, {
                                        href: "mailto:support@qrypt.com",
                                        external: true,
                                        children: "Contact Qrypt support"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CaptchaModal, {
                isOpen: showCaptcha,
                onClose: handleCaptchaClose,
                onSuccess: handleCaptchaSuccess
            })
        ]
    });
}
